import Header from '@/components/layouts/Header';
import PageError from '@/components/layouts/PageError';
import PageLoader from '@/components/layouts/PageLoader';
import {
  useInfiniteAdminSiteLogsQuery,
  type AdminSiteLogData,
} from '@/remote/site-management';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

// 상세 데이터 모달 컴포넌트
function LogDetailModal({
  log,
  isOpen,
  onClose,
}: Readonly<{
  log: AdminSiteLogData | null;
  isOpen: boolean;
  onClose: () => void;
}>) {
  if (!isOpen || !log) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* 모달 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            상세 센서 데이터 - {log.siteId}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="닫기"
          >
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        {/* 모달 내용 */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* 기본 정보 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              기본 정보
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">
                  사이트 ID
                </div>
                <div className="text-lg font-semibold">{log.siteId}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">
                  CDMA 번호
                </div>
                <div className="text-lg">{log.cdmaNumber || '-'}</div>
              </div>
            </div>
          </div>

          {/* 측정 시간 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              측정 시간
            </h3>
            <div className="bg-blue-50 p-3 rounded">
              <div className="text-sm font-medium text-gray-500">시간</div>
              <div className="text-lg">{log.timestampString}</div>
              <div className="text-sm text-gray-600 mt-1">
                Timestamp: {log.timestamp}
              </div>
            </div>
          </div>

          {/* 센서 데이터 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              센서 측정값
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">수위</div>
                <div className="text-lg font-semibold">{log.waterLevel}%</div>
                <div className="text-xs text-gray-600 mt-1">
                  기준: {log.lowWaterLevel}% ~ {log.highWaterLevel}%
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">온도</div>
                <div className="text-lg font-semibold">{log.temperature}°C</div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">
                  약품 레벨
                </div>
                <div className="text-lg font-semibold">
                  {log.chemicalLevel}%
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  기준: {log.lowChemicalLevel}% ~ {log.highChemicalLevel}%
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">유입량</div>
                <div className="text-lg font-semibold">{log.inflow}</div>
              </div>
            </div>
          </div>

          {/* 상태 정보 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              장비 상태
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">
                  모터 상태
                </div>
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                    log.motorRun
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {log.motorRun ? 'ON' : 'OFF'}
                </div>
              </div>
              {log.motor1Status && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm font-medium text-gray-500">
                    모터1 상태
                  </div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                      log.motor1Status
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {log.motor1Status ? 'ON' : 'OFF'}
                  </div>
                </div>
              )}
              {log.motor2Status && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm font-medium text-gray-500">
                    모터2 상태
                  </div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                      log.motor2Status
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {log.motor2Status ? 'ON' : 'OFF'}
                  </div>
                </div>
              )}
              {log.motor3Status && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm font-medium text-gray-500">
                    모터3 상태
                  </div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                      log.motor3Status
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {log.motor3Status ? 'ON' : 'OFF'}
                  </div>
                </div>
              )}
              {log.circuitBreakerError && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm font-medium text-gray-500">
                    차단기 상태
                  </div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                      log.circuitBreakerError
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {log.circuitBreakerError ? 'ON' : 'OFF'}
                  </div>
                </div>
              )}
              {log.externalError && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm font-medium text-gray-500">
                    외부 에러 상태
                  </div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                      log.externalError
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {log.externalError ? 'ON' : 'OFF'}
                  </div>
                </div>
              )}
              {log.pressureSensorError && (
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm font-medium text-gray-500">
                    압력 에러
                  </div>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                      log.pressureSensorError
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {log.pressureSensorError ? 'ON' : 'OFF'}
                  </div>
                </div>
              )}
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">
                  에러 코드
                </div>
                <div className="text-lg font-mono">{log.errorCode}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm font-medium text-gray-500">
                  전체 상태
                </div>
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${
                    log.hasNoError
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {log.hasNoError ? '정상' : '오류'}
                </div>
              </div>
            </div>
          </div>

          {/* 전체 JSON 데이터 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              전체 데이터 (JSON)
            </h3>
            <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm whitespace-pre-wrap">
                {JSON.stringify(log, null, 2)}
              </pre>
            </div>
          </div>
        </div>

        {/* 모달 푸터 */}
        <div className="border-t border-gray-200 px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// 로그 데이터 행 컴포넌트
const LogRow = forwardRef<
  HTMLTableRowElement,
  { log: AdminSiteLogData; onClick: (log: AdminSiteLogData) => void }
>(({ log, onClick }, ref) => {
  const handleRowClick = () => {
    onClick(log);
  };

  return (
    <tr
      ref={ref}
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={handleRowClick}
      title="클릭하면 상세 정보를 볼 수 있습니다"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="font-medium text-gray-900">{log.siteId}</div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900">{log.cdmaNumber || '-'}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-500">{log.timestampString}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-2 flex-wrap max-w-[400px] gap-2">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              log.motorRun
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            상수도 모터 {log.motorRun ? 'ON' : 'OFF'}
          </span>
          {
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                log.motor1Status
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {log.motor1Status ? '모터1 ON' : '모터1 OFF'}
            </span>
          }
          {
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                log.motor2Status
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {log.motor2Status ? '모터2 ON' : '모터2 OFF'}
            </span>
          }
          {
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                log.motor3Status
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {log.motor3Status ? '모터3 ON' : '모터3 OFF'}
            </span>
          }
          {
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                !log.circuitBreakerError
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {log.circuitBreakerError ? '차단기 ON' : '차단기 OFF'}
            </span>
          }
          {
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                !log.externalError
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {log.externalError ? '외부 에러 ON' : '외부 에러 OFF'}
            </span>
          }
          {
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                log.pressureSensorError
                  ? 'bg-red-100 text-red-800'
                  : 'bg-green-100 text-green-800'
              }`}
            >
              {log.pressureSensorError ? '압력 에러 ON' : '압력 에러 OFF'}
            </span>
          }
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-600">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>수위: {log.waterLevel}%</div>
            <div>온도: {log.temperature}°C</div>
            <div>약품: {log.chemicalLevel}%</div>
            <div>유입: {log.inflow}</div>
          </div>
        </div>
      </td>
    </tr>
  );
});

LogRow.displayName = 'LogRow';

// 로딩 스켈레톤 컴포넌트
function LoadingSkeleton({ rows = 5 }: Readonly<{ rows?: number }>) {
  const skeletonItems = Array.from({ length: rows }, (_, i) => ({
    id: `skeleton-${i}`,
  }));

  return (
    <>
      {skeletonItems.map((item) => (
        <tr key={item.id} className="animate-pulse">
          <td className="px-6 py-4">
            <div className="h-4 bg-gray-200 rounded w-16"></div>
          </td>
          <td className="px-6 py-4">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
          </td>
          <td className="px-6 py-4">
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </td>
          <td className="px-6 py-4">
            <div className="flex items-center space-x-2">
              <div className="h-6 bg-gray-200 rounded-full w-12"></div>
              <div className="h-6 bg-gray-200 rounded-full w-16"></div>
            </div>
          </td>
          <td className="px-6 py-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

export default function RawDataPage() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteAdminSiteLogsQuery(50);

  const observerRef = useRef<IntersectionObserver | null>(null);

  // 모달 상태 관리
  const [selectedLog, setSelectedLog] = useState<AdminSiteLogData | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const handleLogClick = (log: AdminSiteLogData) => {
    setSelectedLog(log);
    setIsDetailModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedLog(null);
  };

  // 무한 스크롤 옵저버 설정
  const lastElementRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // 모든 페이지의 데이터를 하나의 배열로 합치기
  const allLogs = data?.pages.flatMap((page) => page.logs) || [];
  const totalCount = data?.pages[0]?.pagination.totalItems || 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <PageLoader
          text="원시 데이터를 불러오고 있습니다"
          subText="잠시만 기다려 주세요."
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <PageError
          text="데이터를 불러오는 중 오류가 발생했습니다"
          subText={error?.message || '잠시 후 다시 시도해주세요.'}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* 페이지 헤더 */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  개발 데이터
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  모든 사이트의 실시간 센서 데이터 (총{' '}
                  {totalCount.toLocaleString()}건)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 테이블 영역 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사이트 ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CDMA 번호
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    측정 시간
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    센서 데이터
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allLogs.map((log, index) => {
                  const isLastElement = index === allLogs.length - 1;
                  return (
                    <LogRow
                      key={`${log.siteId}-${log.timestamp}-${index}`}
                      log={log}
                      onClick={handleLogClick}
                      ref={isLastElement ? lastElementRef : undefined}
                    />
                  );
                })}

                {/* 로딩 스켈레톤 */}
                {isFetchingNextPage && <LoadingSkeleton rows={3} />}
              </tbody>
            </table>
          </div>

          {/* 더 이상 로드할 데이터가 없을 때 메시지 */}
          {!hasNextPage && allLogs.length > 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              모든 데이터를 불러왔습니다.
            </div>
          )}

          {/* 데이터가 없을 때 메시지 */}
          {allLogs.length === 0 && !isLoading && (
            <div className="text-center py-16">
              <div className="text-gray-500 text-lg mb-2">
                로그 데이터가 없습니다.
              </div>
              <div className="text-gray-400 text-sm">
                아직 수집된 로그 데이터가 없거나 권한이 없을 수 있습니다.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 상세 데이터 모달 */}
      <LogDetailModal
        log={selectedLog}
        isOpen={isDetailModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
